import { motion } from "framer-motion";
import { ShoppingBag, ShoppingCart, Sparkles, Star } from "lucide-react";

const PRODUCT_IMG =
  "https://customer-assets.emergentagent.com/job_ahaario-snacks/artifacts/tu7itdkc_EFAC8672-9F82-43BE-AF88-AF121CF76BC0%202.PNG";
const AMAZON_URL = "https://amzn.in/d/02dgPPzh";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  return (
    <section
      id="top"
      className="relative pt-32 lg:pt-36 pb-20 lg:pb-28 overflow-hidden bg-white"
      data-testid="hero-section"
    >
      {/* Decorative blobs */}
      <div className="blob absolute -top-32 -left-24 h-[420px] w-[420px] rounded-full bg-[#A7D129]/40" />
      <div className="blob absolute -bottom-24 right-0 h-[380px] w-[380px] rounded-full bg-[#3E7D2B]/25" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        {/* Text */}
        <div>
          <motion.div
            initial="hidden"
            animate="show"
            custom={0}
            variants={fadeUp}
            className="inline-flex items-center gap-2 bg-[#F2E8D5] text-[#3E7D2B] px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] font-bold border border-[#3E7D2B]/15"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Premium Raw Makhana
          </motion.div>

          <motion.h1
            initial="hidden"
            animate="show"
            custom={1}
            variants={fadeUp}
            className="font-display font-black text-[#13240D] mt-6 text-5xl md:text-6xl lg:text-7xl leading-[1.02]"
          >
            Healthy Snacking{" "}
            <span className="relative inline-block">
              <span className="relative z-10">Made Simple</span>
              <span className="absolute left-0 bottom-1 lg:bottom-2 h-3 lg:h-4 w-full bg-[#A7D129]/60 -z-0 rounded-sm" />
            </span>
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="show"
            custom={2}
            variants={fadeUp}
            className="mt-7 text-lg md:text-xl text-[#4A5D44] max-w-xl leading-relaxed"
          >
            Premium Makhana that&rsquo;s light, crunchy and wholesome. Handpicked
            from Bihar, packed with protein &mdash; guilt-free goodness for every
            craving.
          </motion.p>

          <motion.div
            initial="hidden"
            animate="show"
            custom={3}
            variants={fadeUp}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a
              href={AMAZON_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-shop-amazon-btn"
              className="group inline-flex items-center justify-center gap-2 bg-[#3E7D2B] hover:bg-[#2D5C1F] text-white rounded-full px-8 py-4 font-bold transition-all duration-300 hover:-translate-y-1 hover:shadow-xl shadow-[#3E7D2B]/25"
            >
              <ShoppingBag className="h-5 w-5" />
              Shop on Amazon
              <span className="ml-1 transition-transform group-hover:translate-x-1">→</span>
            </a>
            <button
              type="button"
              data-testid="hero-blinkit-btn"
              disabled
              className="inline-flex items-center justify-center gap-2 bg-white border-2 border-[#3E7D2B] text-[#3E7D2B] rounded-full px-8 py-4 font-bold transition-all duration-300 hover:bg-[#3E7D2B] hover:text-white relative cursor-not-allowed opacity-90"
              title="Coming soon on Blinkit"
            >
              <ShoppingCart className="h-5 w-5" />
              Available on Blinkit
              <span className="absolute -top-2 -right-2 bg-[#A7D129] text-[#13240D] text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border border-white">
                Soon
              </span>
            </button>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="show"
            custom={4}
            variants={fadeUp}
            className="mt-12 flex items-center gap-6"
          >
            <div className="flex -space-x-3">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="h-10 w-10 rounded-full border-2 border-white shadow-sm"
                  style={{
                    background: `linear-gradient(135deg, ${
                      ["#A7D129", "#3E7D2B", "#F2E8D5", "#13240D"][i - 1]
                    }, #fff)`,
                  }}
                />
              ))}
            </div>
            <div>
              <div className="flex items-center gap-1 text-[#3E7D2B]">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-[#3E7D2B]" />
                ))}
              </div>
              <p className="text-sm text-[#4A5D44] font-semibold mt-1">
                Loved by 10,000+ healthy snackers across India
              </p>
            </div>
          </motion.div>
        </div>

        {/* Image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
          className="relative flex justify-center lg:justify-end"
          data-testid="hero-product-image-wrap"
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-[80%] w-[80%] rounded-full bg-gradient-to-br from-[#A7D129]/40 to-[#3E7D2B]/10 blur-3xl" />
          </div>
          <div className="relative float-soft">
            <img
              src={PRODUCT_IMG}
              alt="Ahaario Premium Raw Makhana 200g pouch"
              className="relative z-10 w-[320px] sm:w-[380px] md:w-[440px] lg:w-[480px] drop-shadow-[0_30px_50px_rgba(62,125,43,0.35)]"
              data-testid="hero-product-image"
            />
          </div>

          {/* Floating tags */}
          <motion.div
            initial={{ opacity: 0, x: -30, y: 10 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="absolute top-10 left-2 sm:left-6 lg:left-0 bg-white rounded-2xl px-4 py-3 shadow-xl border border-[#E8EDE6] flex items-center gap-3"
          >
            <div className="h-9 w-9 rounded-full bg-[#A7D129]/20 flex items-center justify-center">
              <span className="text-[#3E7D2B] font-display font-black">9g</span>
            </div>
            <div>
              <p className="text-xs font-bold text-[#13240D]">Plant Protein</p>
              <p className="text-[10px] text-[#4A5D44]">per serving</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30, y: -10 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="absolute bottom-12 right-2 sm:right-6 lg:right-0 bg-[#13240D] text-white rounded-2xl px-4 py-3 shadow-2xl flex items-center gap-3"
          >
            <Sparkles className="h-5 w-5 text-[#A7D129]" />
            <div>
              <p className="text-xs font-bold">100% Natural</p>
              <p className="text-[10px] text-white/70">Handpicked &amp; pure</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
